import { Button } from '@app/components/various/Button';
import { FocusTrap } from '@app/components/various/FocusTrap';
import { CancelSmall, ChevronLeft, Filters as FiltersIcon } from '@app/components/various/Icons';
import { SearchFilter } from '@app/components/various/SearchFilter';
import { SkeletonLayout } from '@app/components/various/SkeletonLayout';
import { FiltersPanelSkeleton } from '@app/components/various/SkeletonLayout/layouts/FiltersPanelSkeleton';
import { BREAKPOINTS } from '@app/constants/breakpoints';
import { cn } from '@app/utils/cn';
import { useIsScreenSize, useOnEscHandler, usePageScrolling } from '@app/utils/hooks';
import { Filter, Product, ProductFilters } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CSSTransition } from 'react-transition-group';

import { FiltersSummary } from '../FiltersSummary';
import { StockSwitcher } from '../StockSwitcher';

import { FiltersPanelGroups } from './FiltersPanelGroups';

interface Props {
  isLoading: boolean;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  onFilterChange: (newFilters: ProductFilters) => void;
  handleToggleFilter: (filterField: string, isExpanded: boolean) => void;
  itemsCount: number;
  showAddAll?: boolean;
  showStockSwitcher?: boolean;
  filters: ProductFilters;
  attributes: Filter.Field<string>[];
  onDrawerClose: () => void;
  expandedFilters: Record<string, boolean>;
  productsDetails?: Record<string, Product.Full>;
  allProductsIds?: string[];
  hasHeaderBanner?: boolean;
  showBarcodeScanner?: boolean;
  hasBreadcrumbs?: boolean;
}

export const FiltersPanel = ({
  isLoading,
  hasActiveFilters,
  onClearFilters,
  itemsCount,
  showAddAll = false,
  onFilterChange,
  handleToggleFilter,
  showStockSwitcher = false,
  filters,
  attributes,
  onDrawerClose,
  expandedFilters,
  productsDetails,
  allProductsIds,
  hasHeaderBanner,
  showBarcodeScanner,
  hasBreadcrumbs,
}: Props) => {
  const { t } = useTranslation(['common']);
  const isTablet = useIsScreenSize(BREAKPOINTS.LG);
  const [isPinned, setIsPinned] = React.useState(false);
  const isOpenPDM = location.search.includes('showProduct');
  const areFiltersExpanded = !isOpenPDM && isPinned;

  const { setPageScrollable } = usePageScrolling();
  useOnEscHandler(document.documentElement, () => setIsPinned(false), areFiltersExpanded);

  React.useEffect(() => {
    setPageScrollable(!isTablet || !areFiltersExpanded);
  }, [isTablet, areFiltersExpanded, setPageScrollable]);

  const isTopBarHidden = !hasHeaderBanner && !hasBreadcrumbs;
  const hasOnlyBanner = hasHeaderBanner && !hasBreadcrumbs;
  const hasBannerAndBreadcrumbs = hasHeaderBanner && hasBreadcrumbs;
  const hasOnlyBreadcrumbs = !hasHeaderBanner && hasBreadcrumbs;

  return (
    <>
      {isTablet && (
        <div
          className={cn('box fixed left-0 w-6 p-0 transition-[top,height]', {
            'group-[.scrollingDown]/body:top-[calc(1.5rem+var(--headerHeight)+var(--breadcrumbsHeight))]': hasOnlyBreadcrumbs,
            'group-[.scrollingDown]/body:top-[calc(5rem+var(--breadcrumbsHeight))] group-[.scrollingDown]/body:h-[calc(100%-var(--headerWithBannerHeight)-6rem-var(--breadcrumbsHeight))]':
              hasBannerAndBreadcrumbs,
            'group-[.scrollingDown]/body:top-[calc(var(--headerHeight)+1.25rem)]': isTopBarHidden,
            'group-[.scrollingDown]/body:top-[calc(var(--headerHeight)+4rem)] group-[.scrollingDown]/body:h-[calc(100%-var(--headerWithBannerHeight)-6.5rem)]':
              hasOnlyBanner,
            'h-[calc(100%-10.25rem-var(--headerHeight))]': !hasHeaderBanner,
            'h-[calc(100%-10.25rem-var(--headerWithBannerHeight))]': hasHeaderBanner,
            'top-[calc(var(--headerHeight)+4.75rem)]': isTopBarHidden,
            'top-[calc(var(--headerHeight)+5.75rem+var(--breadcrumbsHeight))]': hasOnlyBreadcrumbs,
            'top-[calc(var(--headerWithBannerHeight)+4.75rem)]': hasOnlyBanner,
            'top-[calc(var(--headerWithBannerHeight)+var(--breadcrumbsHeight)+5.75rem)]': hasBannerAndBreadcrumbs,
          })}
        >
          <Button
            aria-label={t('common:expand_filters')}
            icon={FiltersIcon}
            size="medium"
            className="ml-2"
            onClick={() => setIsPinned(true)}
          />
        </div>
      )}
      {isTablet && (
        <CSSTransition
          in={areFiltersExpanded}
          unmountOnExit
          timeout={150}
          classNames={{
            enter: 'opacity-0',
            enterActive: '!opacity-100 transition-opacity',
            exit: 'opacity-100',
            exitActive: '!opacity-0 transition-opacity',
          }}
        >
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div className="fixed inset-0 z-overlay" onClick={() => setIsPinned(false)}>
            <div
              className={cn('fixed inset-0 bg-gradient-to-b from-transparent to-white/50 to-[2%]', {
                'top-[calc(var(--headerHeight)+var(--breadcrumbsHeight))]': hasOnlyBreadcrumbs,
                'top-[calc(var(--headerWithBannerHeight)+var(--breadcrumbsHeight))] group-[.scrollingDown]/body:top-0':
                  hasBannerAndBreadcrumbs,
                'top-[var(--headerHeight)]': isTopBarHidden,
                'top-[var(--headerWithBannerHeight)] group-[.scrollingDown]/body:top-0': hasOnlyBanner,
              })}
            />
          </div>
        </CSSTransition>
      )}
      <FocusTrap isActive={areFiltersExpanded}>
        <div
          {...(isTablet && !areFiltersExpanded ? { inert: '' } : {})}
          className={cn(
            'fixed bottom-0 left-0 top-[calc(var(--headerHeight))] flex flex-col overflow-hidden transition-[top,transform] group-[.scrollingDown]/body:top-0',
            {
              '-translate-x-full': isTablet && !areFiltersExpanded,
              'bg-neutral-96 shadow-3h': isTablet && areFiltersExpanded,
              'lg:top-[--headerWithBannerHeightOnDesktop]': hasHeaderBanner,
              'lg:top-[calc(var(--headerWithBannerHeightOnDesktop)+var(--breadcrumbsHeight)+0.75rem)]': hasBannerAndBreadcrumbs,
              'top-[--headerWithBannerHeight]': hasHeaderBanner,
              'top-[calc(var(--headerHeight)+var(--breadcrumbsHeight)+0.75rem)]': hasBreadcrumbs,
              'top-[var(--headerHeight)]': !hasBreadcrumbs,
              'z-filters': isTablet,
            },
          )}
        >
          <div className="mb-3 mt-2 h-14 pl-4 pr-4 lg:mb-2 lg:pl-8">
            <div className="mb-1 flex items-center justify-between gap-1">
              <h2 className="heading-3">{t('common:filter_other')}</h2>
              {hasActiveFilters && (
                <Button onClick={onClearFilters} icon={CancelSmall} variant="text" color="secondary" className="text-error-45">
                  {t('common:reset')}
                </Button>
              )}
            </div>
            <FiltersSummary
              showAddAll={showAddAll && hasActiveFilters}
              itemsCount={itemsCount}
              filters={filters}
              allProductsIds={allProductsIds}
            />
          </div>
          <div className="h-full w-[--filterBarWidth] overflow-y-hidden">
            <div className="mt-[0.3rem] h-full overflow-y-auto overflow-x-hidden lg:mt-0.5">
              <div className="flex w-[--filterBarWidth] flex-col gap-2 pb-4 pl-4 pr-3 lg:pl-8">
                {showStockSwitcher && <StockSwitcher onStockSwitchConfirmation={onDrawerClose} productsDetails={productsDetails} />}
                <SearchFilter
                  onShowBarcodeScanner={onDrawerClose}
                  value={filters.search}
                  onFilterChange={onFilterChange}
                  showBarcodeScanner={showBarcodeScanner}
                />
                <SkeletonLayout isLoading={isLoading} skeleton={<FiltersPanelSkeleton />}>
                  <FiltersPanelGroups
                    attributes={attributes}
                    filters={filters}
                    expandedFilters={expandedFilters}
                    onFilterChange={onFilterChange}
                    onToggleFilter={handleToggleFilter}
                  />
                </SkeletonLayout>
              </div>
            </div>
          </div>
        </div>
        {isTablet && (
          <CSSTransition
            classNames={{
              enter: 'opacity-0 !translate-x-0',
              enterActive: '!opacity-100 !translate-x-[--filterBarWidth] transition-[opacity,transform]',
              exit: 'opacity-100 translate-x-[--filterBarWidth]',
              exitActive: '!opacity-0 !translate-x-0 transition-[opacity,transform]',
            }}
            in={areFiltersExpanded}
            unmountOnExit
            timeout={150}
          >
            <div
              className={cn(
                'fixed left-0 top-[calc(var(--filtersButtonTop)+var(--breadcrumbsHeight))] z-filters -ml-4 translate-x-[--filterBarWidth] group-[.scrollingDown]/body:top-[calc(var(--productsListingHeaderHeight)+var(--breadcrumbsHeight))]',
                {
                  'top-[--filtersButtonTopWithBanner]': hasHeaderBanner,
                  'top-[calc(var(--filtersButtonTop)+var(--breadcrumbsHeight)+1rem)]': hasBreadcrumbs,
                  'top-[calc(var(--filtersButtonTopWithBanner)+var(--breadcrumbsHeight)+1rem)]': hasBannerAndBreadcrumbs,
                  'top-[var(--filtersButtonTop)]': !hasBreadcrumbs,
                },
              )}
            >
              <Button size="medium" onClick={() => setIsPinned(false)} icon={ChevronLeft} aria-label={t('common:collapse_filters')} />
            </div>
          </CSSTransition>
        )}
      </FocusTrap>
    </>
  );
};
